import { AssessmentRuleService, PricingService, ServerService, TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { EC2InstanceTypeResult, EC2InstanceTypes, ServerBulkUpdateParams } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import {
    Autosuggest, AutosuggestProps, Box, Button, ExpandableSection, Grid, NonCancelableCustomEvent, Select, SelectProps, SpaceBetween
} from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { constants } from '../../../../common/Localization';
import AWSCATTable from '../../../../common/table/Table-index';
import { ColumnDefinition, ColumnTypes } from '../../../../common/table/types';
import { ErrorWithResponse, MatchParam } from '../../../../common/types';
import { PortfolioContext } from '../../../../contexts/PortfolioContext';
import { TcoContext } from '../../../../contexts/TcoContext';
import { overrideFieldOptions } from '../../common/Common';
import { updateTCOAssessment } from '../../TCOHelper';

type Props = {
    instanceTypeResults: EC2InstanceTypeResult[];
} & FlashContextInterface;

const assessmentRuleService = new AssessmentRuleService();
const pricingService = new PricingService();
const serverService = new ServerService();
const tcoService = new TcoService();

function EC2Instances({ instanceTypeResults, flash }: Props): JSX.Element | null {

    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });
    const portfolioId = match?.params?.portfolioId;

    const isMounted = useIsMounted();

    const { portfolio } = useContext(PortfolioContext);
    const { assessment, assessmentMetadatas, updateAssessment, updateTcoLoading } = useContext(TcoContext);

    const [ loading, setLoading ] = useState(false);
    const [ bulkUpdateLoading, setBulkUpdateLoading ] = useState(false);
    const [ bulkUpdateSelectedField, setBulkUpdateSelectedField ] = useState<SelectProps.Option | null>(null);
    const [ bulkUpdateSelectedInstance, setBulkUpdateSelectedInstance ] = useState<string>('');
    const [ ec2InstanceTypesByRegion, setEc2InstanceTypesByRegion ] = useState<EC2InstanceTypes>();
    const [ columnDefinition, setColumnDefinition ] = useState<ColumnDefinition<EC2InstanceTypeResult>[]>([]);
    const [ selectedInstances, setSelectedInstances ] = useState<EC2InstanceTypeResult[]>([]);

    const loadRules = async () => {
        if (portfolio?.assessmentId) {
            try {
                setLoading(true);
                const rules = await assessmentRuleService.list(portfolio.assessmentId);
                if (!isMounted()) {
                    return;
                }
                const rulesNameMap: Map<string, string> = new Map();
                rules.forEach(rule => {
                    rulesNameMap.set(rule.ruleId, rule.name);
                });
                const columnDefinitionToUpdate: ColumnDefinition<EC2InstanceTypeResult>[] = [
                    { header: constants.SERVER_ID, id: 'id', type: ColumnTypes.TEXT, cell: item => item.id },
                    { header: rulesNameMap.get('502'), id: '502', type: ColumnTypes.TEXT, cell: item => item[502] },
                    { header: rulesNameMap.get('503'), id: '503', type: ColumnTypes.TEXT, cell: item => item[503] },
                    { header: rulesNameMap.get('504'), id: '504', type: ColumnTypes.TEXT, cell: item => item[504] },
                    {
                        header: constants.OVERRIDDEN,
                        type: ColumnTypes.BOOLEAN,
                        id: 'override',
                        data: item => item.override ? constants.TRUE : constants.FALSE,
                        cell: item => item.override ? constants.TRUE : constants.FALSE,
                    },
                ];
                const colWithText = columnDefinitionToUpdate.map(col => (
                    {
                        ...col,
                        text: col.header?.toString(),
                    }
                ));
                setColumnDefinition(colWithText);
            }
            catch (err) {
                if (isMounted()) {
                    flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_RULES);
                }
            } finally {
                if (isMounted()) {
                    setLoading(false);
                }
            }
        }
    };

    const loadInstanceOptions = async () => {
        const region = assessment?.configuration.tcoAssumptions.awsRegion;
        if (region) {
            try {
                setBulkUpdateLoading(true);
                const instances = await pricingService.getEC2InstanceTypes(region);
                if (isMounted()) {
                    setEc2InstanceTypesByRegion(instances);
                }
            } catch (err) {
                if (isMounted()) {
                    flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_EC2_INSTANCE_TYPES);
                }
            } finally {
                if (isMounted()) {
                    setBulkUpdateLoading(false);
                }
            }
        }
    };

    useEffect(() => {
        loadInstanceOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ portfolio.assessmentId ]);

    const getAutoSuggestOptions = ():AutosuggestProps.Option[] => {
        const options = [
            {value: constants.UNDO_MANUAL_OVERRIDE},
        ];

        if (assessment && ec2InstanceTypesByRegion?.[assessment.configuration.tcoAssumptions.awsRegion as keyof EC2InstanceTypes]) {
            ec2InstanceTypesByRegion[assessment.configuration.tcoAssumptions.awsRegion as keyof EC2InstanceTypes].forEach(instance => {
                options.push({value: instance});
            });
        }

        return options;
    };
    
    const onFieldUpdate = (event: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
        setBulkUpdateSelectedField(event.detail.selectedOption);
    };

    const onInstanceSelectionChange = (event: NonCancelableCustomEvent<AutosuggestProps.ChangeDetail>) => {
        setBulkUpdateSelectedInstance(event.detail.value);
    };

    const onTableSelectionUpdate = (items: readonly EC2InstanceTypeResult[] | undefined) => {
        setSelectedInstances(items ? [...items] : []);
    };

    const onUpdateClick = async () => {
        try {
            setBulkUpdateLoading(true);
            updateTcoLoading(true);
            const serverIds: string[] = selectedInstances.map(instance => instance.id);
            const ec2AssessmentId = (assessment && assessmentMetadatas &&
                assessmentMetadatas.find(metadata => metadata.id === assessment.id)?.refAwsEc2Assessment) ?? '';

            if (!serverIds.length || !bulkUpdateSelectedField || !bulkUpdateSelectedInstance) {
                flash.alert(FlashType.error, constants.ERROR_OVERRIDING_EC2_RECOMMENDATION, constants.INVALID_INPUT);
                return;
            }
            else if (portfolioId && assessment) {
                const conditions: ServerBulkUpdateParams = {
                    portfolioId,
                    serverIds,
                    fieldName: `ec2RecommendationOverride${bulkUpdateSelectedField.value}`,
                    fieldValue: bulkUpdateSelectedInstance,
                };

                if (bulkUpdateSelectedInstance === constants.UNDO_MANUAL_OVERRIDE) {
                    conditions.fieldValue = null;
                }
                else {
                    const validations = {
                        portfolioId,
                        servers: serverIds,
                        instanceType: bulkUpdateSelectedInstance,
                        configuration: assessment.configuration,
                    };

                    await tcoService.validateOverrideServerRecommendation(ec2AssessmentId, validations);
                }
                await serverService.update(conditions);
                if (isMounted()) {
                    const updatedAssessment = await updateTCOAssessment(portfolioId, assessment, tcoService);
                    updateAssessment(updatedAssessment);
                    flash.alert(FlashType.success, constants.UPDATE_SUCCESSFUL, '');
                }
            }
        }
        catch (err) {
            let errorMsg = (err as ErrorWithResponse).response?.data?.message ?? (err as Error).message;
            if (errorMsg.startsWith('Instance type')) {
                errorMsg += constants.INSTANCE_TYPE_ERROR;
            }
            flash.alert(FlashType.error, errorMsg, constants.VALIDATION_FAILED);
        } finally {
            setBulkUpdateLoading(false);
            updateTcoLoading(false);
        }
    };

    const overrideRecommendation: JSX.Element = (
        <ExpandableSection header={constants.OVERRIDE_EC2_RECOMMENDATION_HEADER} defaultExpanded={true}>
            <Grid gridDefinition={[{colspan: 4}, {colspan: 4}, {colspan: 4}]}>
                <Select
                    disabled={bulkUpdateLoading}
                    selectedOption={bulkUpdateSelectedField}
                    options={overrideFieldOptions}
                    onChange={onFieldUpdate}
                    placeholder={constants.RECOMMENDATION_TYPE}
                />
                <Autosuggest
                    disabled={bulkUpdateLoading}
                    options={getAutoSuggestOptions()}
                    value={bulkUpdateSelectedInstance}
                    onChange={onInstanceSelectionChange}
                    placeholder={constants.LIST_OF_INSTANCE_TYPE}
                    enteredTextLabel={value => `Use: '${value}'`}
                />
                <Box float='right'>
                    <Button
                        data-id='btn-ec2-override'
                        variant='primary'
                        onClick={onUpdateClick}
                        loading={bulkUpdateLoading}
                        disabled={bulkUpdateSelectedInstance === '' || !bulkUpdateSelectedField || !selectedInstances.length}
                    >
                        {`${constants.UPDATE_BUTTON_TEXT} ${selectedInstances.length} ${constants.ITEM_S}`}
                    </Button>
                </Box>
            </Grid>
        </ExpandableSection>
    );

    return (
        <SpaceBetween size='m'>
            {overrideRecommendation}
            <AWSCATTable
                resourceName={constants.EC2_INSTANCE_TYPE_DETAILS}
                columnDefinitions={columnDefinition}
                items={instanceTypeResults ?? []}
                loading={loading}
                onSelectionChange={onTableSelectionUpdate}
                autoGenerateVisibleContent
                enableAutoSorting
                resizeable={false} // explicitly not allowing resize since there are few columns and all get crammed to the left
                selectionType='multi'
                useDefaultDownload
            />
        </SpaceBetween>
    );
}

export default withFlashContext(EC2Instances);
